import { useEffect, useState, useCallback } from "react";
import { FaEye, FaCalendarAlt, FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaChartBar, FaDownload } from "react-icons/fa";
import PageTitle from "../../../../jsx/layouts/PageTitle";
import {
  getAttendanceReport,
  getAttendanceStats,
  getMyTrainingSessions,
  exportAttendanceCsv,
} from "../../../../services/adminApiService/batchManagementApi/BatchManagementApiService";
import AttendanceDetailModal from "./AttendanceDetailModal";
import AttendanceModal from "../Calendar/AttendanceModal";

// ─── Coach View ───────────────────────────────────────────────────────────────

const CoachView = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchSessions = useCallback(() => {
    setLoading(true);
    getMyTrainingSessions()
      .then((res) => {
        const data = res?.data?.data;
        setSessions(Array.isArray(data) ? data : []);
      })
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleTakeAttendance = (session) => {
    setSelectedSession(session);
    setShowModal(true);
  };

  const handleModalHide = () => {
    setShowModal(false);
    setSelectedSession(null);
    fetchSessions();
  };


  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border" style={{ color: "#8757e8" }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-5">
        <FaCalendarAlt size={48} style={{ color: "#ccc" }} />
        <p className="mt-3 text-muted">No training sessions assigned to you.</p>
      </div>
    );
  }

  return (
    <>
      <div className="row g-3">
        {sessions.map((session) => (
          <div
            key={session.id}
            className="col-12 col-sm-6 col-lg-4"
          >
            <div
              className="card h-100"
              style={{
                borderRadius: "12px",
                boxShadow: "0 2px 12px rgba(135,87,232,0.10)",
                border: "1px solid #f0eaff",
                overflow: "hidden",
              }}
            >
              {/* Card header strip */}
              <div style={{ height: "4px", background: "#8757e8" }} />

              <div className="card-body d-flex flex-column p-4">
                {/* Training name */}
                <div
                  className="fw-bold mb-2"
                  style={{ fontSize: "17px", color: "#222", lineHeight: "1.3" }}
                >
                  {session.training_name || "Untitled Training"}
                </div>

                {/* Today's session date */}
                <div className="d-flex align-items-center mb-1" style={{ fontSize: "13px" }}>
                  <FaCalendarAlt size={13} className="me-2" style={{ flexShrink: 0, color: "#8757e8" }} />
                  <strong style={{ color: "#333" }}>
                    Today:{" "}
                    {new Date((session.session_date || session.training_date) + "T00:00:00").toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}
                  </strong>
                  {session.day_number && session.total_days && (
                    <span className="ms-2 text-muted" style={{ fontSize: "11px" }}>
                      (Day {session.day_number} of {session.total_days})
                    </span>
                  )}
                </div>
                {/* Full training range — subtle */}
                <div className="d-flex align-items-center mb-2" style={{ fontSize: "11px", color: "#bbb" }}>
                  <FaCalendarAlt size={10} className="me-2" />
                  {session.training_date} – {session.training_end_date || "ongoing"}
                </div>

                {/* Venue */}
                <div className="d-flex align-items-center mb-3 text-muted" style={{ fontSize: "13px" }}>
                  <FaMapMarkerAlt size={13} className="me-2" style={{ flexShrink: 0 }} />
                  {session.venue_name || "—"}
                </div>

                {/* Attendance badge */}
                <div className="mb-3">
                  {session.has_attendance_today ? (
                    <span
                      className="badge"
                      style={{
                        background: "#d4edda",
                        color: "#155724",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                      }}
                    >
                      <FaCheckCircle size={11} className="me-1" />
                      Attendance Taken Today
                    </span>
                  ) : (
                    <span
                      className="badge"
                      style={{
                        background: "#e9ecef",
                        color: "#6c757d",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                      }}
                    >
                      Not Taken
                    </span>
                  )}
                </div>

                {/* Spacer */}
                <div className="flex-grow-1" />

                {/* Action button */}
                <button
                  className="btn w-100 mt-2"
                  style={{
                    background: "#8757e8",
                    color: "#fff",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "14px",
                    padding: "10px",
                  }}
                  onClick={() => handleTakeAttendance(session)}
                >
                  Take Attendance
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && selectedSession && (
        <AttendanceModal
          show={showModal}
          onHide={handleModalHide}
          training={{
            id: selectedSession.id,
            training_name: selectedSession.training_name,
            training_date: selectedSession.training_date,
            training_end_date: selectedSession.training_end_date,
          }}
        />
      )}
    </>
  );
};

// ─── Admin / DSO View ─────────────────────────────────────────────────────────

const StatCard = ({ label, value, icon, accentColor }) => (
  <div className="col-6 col-lg-3">
    <div
      className="card-box"
      style={{
        borderRadius: "12px",
        padding: "20px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
        borderLeft: `4px solid ${accentColor}`,
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div style={{ fontSize: "13px", color: "#888" }}>{label}</div>
        <div style={{ color: accentColor, fontSize: "20px" }}>{icon}</div>
      </div>
      <div style={{ fontSize: "28px", fontWeight: "700", color: "#333" }}>{value ?? "—"}</div>
    </div>
  </div>
);

const AdminDSOView = () => {
  const [stats, setStats] = useState({});
  const [report, setReport] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExportCsv = () => {
    setExporting(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    exportAttendanceCsv(params)
      .then((res) => {
        const rows = res?.data?.data || [];
        if (!rows.length) { alert('No data to export.'); return; }
        const headers = Object.keys(rows[0]);
        const csvContent = [headers, ...rows.map(r => Object.values(r))]
          .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
          .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'attendance_export.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Export failed. Please try again.'))
      .finally(() => setExporting(false));
  };

  const fetchStats = useCallback((params = {}) => {
    setStatsLoading(true);
    getAttendanceStats(params)
      .then((res) => {
        setStats(res?.data?.data || res?.data || {});
      })
      .catch(() => setStats({}))
      .finally(() => setStatsLoading(false));
  }, []);

  const fetchReport = useCallback(
    (page = currentPage) => {
      setReportLoading(true);
      const params = { page };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      getAttendanceReport(params)
        .then((res) => {
          const data = res?.data?.data;
          setReport(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
          if (data?.last_page) setTotalPages(data.last_page);
          else if (data?.total && data?.per_page)
            setTotalPages(Math.ceil(data.total / data.per_page));
        })
        .catch(() => setReport([]))
        .finally(() => setReportLoading(false));
    },
    [currentPage, dateFrom, dateTo]
  );

  useEffect(() => {
    fetchStats();
    fetchReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchReport(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleFilterApply = () => {
    setCurrentPage(1);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    fetchStats(params);
    fetchReport(1);
  };

  const handleFilterReset = () => {
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
    fetchStats();
    fetchReport(1);
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const attendanceRate =
    stats.total_present != null && stats.total_athletes != null && stats.total_athletes > 0
      ? ((stats.total_present / stats.total_athletes) * 100).toFixed(1)
      : stats.attendance_rate != null
      ? stats.attendance_rate
      : "—";

  return (
    <>
      {/* Stats Row */}
      <div className="row g-3 mb-4">
        <StatCard
          label="Total Sessions"
          value={statsLoading ? "…" : stats.total_sessions ?? stats.sessions ?? "—"}
          icon={<FaCalendarAlt />}
          accentColor="#8757e8"
        />
        <StatCard
          label="Total Present"
          value={statsLoading ? "…" : stats.total_present ?? stats.present ?? "—"}
          icon={<FaCheckCircle />}
          accentColor="#28a745"
        />
        <StatCard
          label="Total Absent"
          value={statsLoading ? "…" : stats.total_absent ?? stats.absent ?? "—"}
          icon={<FaTimesCircle />}
          accentColor="#dc3545"
        />
        <StatCard
          label="Attendance Rate"
          value={statsLoading ? "…" : attendanceRate !== "—" ? `${attendanceRate}%` : "—"}
          icon={<FaChartBar />}
          accentColor="#007bff"
        />
      </div>

      {/* Filters */}
      <div className="row g-2 mb-3">
        <div className="col-12 col-md-3">
          <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
            Date From
          </label>
          <input
            type="date"
            className="form-control"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-3">
          <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
            Date To
          </label>
          <input
            type="date"
            className="form-control"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-2 d-flex align-items-end gap-2">
          <button
            className="btn btn-sm w-100"
            style={{ background: "#8757e8", color: "#fff", borderRadius: "8px" }}
            onClick={handleFilterApply}
          >
            Apply
          </button>
          <button
            className="btn btn-sm btn-light w-100"
            style={{ borderRadius: "8px" }}
            onClick={handleFilterReset}
          >
            Reset
          </button>
        </div>
        <div className="col-12 col-md-2 d-flex align-items-end">
          <button
            className="btn btn-sm w-100"
            style={{ background: "#28a745", color: "#fff", borderRadius: "8px" }}
            onClick={handleExportCsv}
            disabled={exporting}
          >
            <FaDownload size={13} className="me-1" />
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card-box p-0" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table className="table mb-0" style={{ width: "100%" }}>
          <thead>
            <tr style={{ background: "#8757e8" }}>
              <th className="text-white fw-semibold py-2 px-3">Training Name</th>
              <th className="text-white fw-semibold py-2 px-3 d-none d-md-table-cell">Sport</th>
              <th className="text-white fw-semibold py-2 px-3 d-none d-md-table-cell">Session Date</th>
              <th className="text-white fw-semibold py-2 px-3 d-none d-md-table-cell">Venue</th>
              <th className="text-white fw-semibold py-2 px-3">Present</th>
              <th className="text-white fw-semibold py-2 px-3">Absent</th>
              <th className="text-white fw-semibold py-2 px-3">Total</th>
              <th className="text-white fw-semibold py-2 px-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {reportLoading ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : report.length > 0 ? (
              report.map((record, idx) => (
                <tr
                  key={`${record.training_id}-${record.session_date}-${idx}`}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  <td className="py-2 px-3 fw-semibold" style={{ maxWidth: "140px", wordBreak: "break-word" }}>
                    {record.training_name || "—"}
                    <div className="d-md-none text-muted" style={{ fontSize: "11px", fontWeight: 400 }}>
                      {[record.sport_name, record.session_date || record.date, record.venue_name || record.venue]
                        .filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td className="py-2 px-3 d-none d-md-table-cell">{record.sport_name || "—"}</td>
                  <td className="py-2 px-3 d-none d-md-table-cell">{record.session_date || record.date || "—"}</td>
                  <td className="py-2 px-3 d-none d-md-table-cell">{record.venue_name || record.venue || "—"}</td>
                  <td className="py-2 px-3">
                    <span
                      className="badge"
                      style={{ background: "#d4edda", color: "#155724", padding: "4px 8px" }}
                    >
                      {record.present_count ?? record.present ?? 0}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span
                      className="badge"
                      style={{ background: "#f8d7da", color: "#721c24", padding: "4px 8px" }}
                    >
                      {record.absent_count ?? record.absent ?? 0}
                    </span>
                  </td>
                  <td className="py-2 px-3 fw-semibold">
                    {record.total ?? record.total_count ?? 0}
                  </td>
                  <td className="py-2 px-3">
                    <button
                      className="btn btn-sm"
                      style={{ background: "#8757e8", color: "#fff", borderRadius: "8px", padding: "6px 10px" }}
                      onClick={() => handleViewDetails(record)}
                      title="View Details"
                    >
                      <FaEye size={15} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  No attendance records found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="d-flex justify-content-between align-items-center px-4 py-3"
            style={{ borderTop: "1px solid #f0f0f0" }}
          >
            <span className="text-muted" style={{ fontSize: "14px" }}>
              Page {currentPage} of {totalPages}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-light btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                style={{ borderRadius: "8px" }}
              >
                Previous
              </button>
              <button
                className="btn btn-light btn-sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                style={{ borderRadius: "8px" }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showDetailModal && selectedRecord && (
        <AttendanceDetailModal
          show={showDetailModal}
          onHide={() => {
            setShowDetailModal(false);
            setSelectedRecord(null);
          }}
          trainingId={selectedRecord.training_id}
          trainingName={selectedRecord.training_name}
        />
      )}
    </>
  );
};

// ─── Root component ───────────────────────────────────────────────────────────

const AttendanceReport = () => {
  const isCoach = localStorage.getItem("category_type") === "2";
  const loginDetails = JSON.parse(localStorage.getItem("login_Details") || "{}");
  const userLevel = loginDetails?.user?.user_level; // 2=admin, 3=DSO

  return (
    <div className="container-fluid p-4">
      <PageTitle activeMenu="Attendance" motherMenu="Academy Management" />
      {isCoach ? <CoachView /> : <AdminDSOView userLevel={userLevel} />}
    </div>
  );
};

export default AttendanceReport;
